/**
 * TCM organ-deficiency ranking → RIFE rebalancing programs (paid layer).
 *
 * The face/tongue read maps each notable zone to a body system with a 0–100
 * score (lower = more stands out / more deficient). This ranks those systems
 * from most to least deficient and pairs each with a curated organ-support RIFE
 * program. The UI shows the ranking, highlights the top-priority organ, and lets
 * the user pick which to play first.
 *
 * Programs are gentle "support / balance / general" protocols sourced from the
 * QiCoil RIFE library (id traces back to that catalog) — association only, never
 * diagnostic. Disease-specific entries are deliberately excluded.
 */

export interface RifeProgram {
  /** Source id in the QiCoil RIFE catalog, for traceability. */
  id: string;
  /** Canonical organ/system this program supports. */
  organ: string;
  /** Human-readable program name. */
  title: string;
  /** Frequencies in Hz, played in order. */
  hz: number[];
}

/**
 * One curated support program per canonical organ. Frequencies are verbatim
 * from the QiCoil RIFE export (rife_frequencies_export.csv) by the listed id.
 */
export const ORGAN_RIFE_PROGRAMS: readonly RifeProgram[] = [
  { id: '3642', organ: 'Heart', title: 'Heart General', hz: [20, 81, 162, 5000] },
  {
    id: '4312',
    organ: 'Liver',
    title: 'Liver Function Balance',
    hz: [33.13, 537, 751, 802, 1550, 1552],
  },
  {
    id: 'spleen-support',
    organ: 'Spleen',
    title: 'Spleen Support',
    hz: [20, 465, 727.5, 802, 880, 1550, 1800, 2170, 2720, 10000],
  },
  {
    id: '4348',
    organ: 'Lung',
    title: 'Lung General',
    hz: [9, 20, 72, 95, 125, 444, 450, 727, 776, 787, 802, 880, 1550, 1865],
  },
  {
    id: '4180',
    organ: 'Kidney',
    title: 'Kidney Tonic General',
    hz: [8, 20, 248, 440, 800, 880, 3000, 5000, 10000],
  },
  {
    id: 'gallbladder-1',
    organ: 'Gallbladder',
    title: 'Gallbladder 1',
    hz: [20, 727, 787, 880, 5000],
  },
  {
    id: 'pancreas-balance-1',
    organ: 'Pancreas',
    title: 'Pancreas Balance 1',
    hz: [1.19, 250, 654, 660, 690, 727.5, 2127.5],
  },
  {
    id: 'adrenal-balance',
    organ: 'Adrenal',
    title: 'Adrenal Gland Balance',
    hz: [20, 537, 1335, 2250, 10000, 12000],
  },
  {
    id: 'bladder-support',
    organ: 'Bladder',
    title: 'Bladder Support',
    hz: [2.57, 352, 727, 787, 800, 880],
  },
  {
    id: 'intestines-inflammation',
    organ: 'Intestines',
    title: 'Intestines Support',
    hz: [105, 727, 787, 791, 832, 880, 1550],
  },
] as const;

/**
 * Keyword → canonical organ. Order matters: more specific terms (gallbladder)
 * must precede broader ones that they contain (bladder). The free-text `system`
 * field from a region read (e.g. "Spleen / digestion", "Kidney / fluids") is
 * matched against the first pattern that hits.
 */
const ORGAN_KEYWORDS: ReadonlyArray<{ organ: string; match: RegExp }> = [
  { organ: 'Gallbladder', match: /gall\s*bladder/i },
  { organ: 'Heart', match: /heart/i },
  { organ: 'Liver', match: /liver/i },
  { organ: 'Spleen', match: /spleen|stomach|digest/i },
  { organ: 'Lung', match: /lung|respir/i },
  { organ: 'Kidney', match: /kidney|adrenal.*kidney/i },
  { organ: 'Pancreas', match: /pancreas/i },
  { organ: 'Adrenal', match: /adrenal/i },
  { organ: 'Bladder', match: /bladder/i },
  { organ: 'Intestines', match: /intestine|bowel|colon/i },
];

const PROGRAM_BY_ORGAN = new Map(ORGAN_RIFE_PROGRAMS.map((p) => [p.organ, p]));

function matchOrgan(system: string): string | null {
  for (const { organ, match } of ORGAN_KEYWORDS) {
    if (match.test(system)) return organ;
  }
  return null;
}

/**
 * Resolve the curated RIFE program for a free-text body-system label
 * (e.g. "Heart / circulation" → Heart General). Returns null when the label
 * doesn't map to a supported organ. Lets the body-systems panel offer a
 * per-line rebalance without re-running the full deficiency ranking.
 */
export function programForSystem(system: string): RifeProgram | null {
  const organ = matchOrgan(system);
  return organ ? (PROGRAM_BY_ORGAN.get(organ) ?? null) : null;
}

export interface OrganDeficiency {
  /** Canonical organ. */
  organ: string;
  /** Aggregated 0–100 score across contributing regions (lower = more deficient). */
  score: number;
  /** Region labels that mapped to this organ (e.g. ["Sides", "Cheeks"]). */
  regions: string[];
  /** Curated RIFE program to rebalance this organ. */
  program: RifeProgram;
}

/**
 * Rank deficient organs from a combined face + tongue region read.
 *
 * Only regions with a numeric score and a recognizable system contribute.
 * Scores for the same organ are averaged. Results are sorted ascending —
 * index 0 is the most deficient (highest rebalancing priority).
 */
export function rankOrganDeficiencies(
  regions: ReadonlyArray<{ region: string; system: string; score?: number }>,
): OrganDeficiency[] {
  const byOrgan = new Map<string, { scores: number[]; regions: string[] }>();

  for (const r of regions) {
    if (typeof r.score !== 'number') continue;
    const organ = matchOrgan(r.system);
    if (!organ || !PROGRAM_BY_ORGAN.has(organ)) continue;
    const entry = byOrgan.get(organ) ?? { scores: [], regions: [] };
    entry.scores.push(r.score);
    entry.regions.push(r.region);
    byOrgan.set(organ, entry);
  }

  const ranked: OrganDeficiency[] = [];
  for (const [organ, { scores, regions: regionLabels }] of byOrgan) {
    const score = scores.reduce((a, b) => a + b, 0) / scores.length;
    ranked.push({ organ, score, regions: regionLabels, program: PROGRAM_BY_ORGAN.get(organ)! });
  }

  return ranked.sort((a, b) => a.score - b.score);
}
